import Papa from 'papaparse'
import { useEffect, useMemo, useState } from 'react'

import { ButtonRounded, ControlledInputDebounced } from '@apps-common/components/elements'
import {
  ModalBodyInfiniteScrollable,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { PageResultsCount } from '@apps-common/components/page'
import { ModalDismiss } from '@apps-common/config/modal'
import { toastError, toastSuccess, toastWarning } from '@apps-common/lib/toast'
import moment from 'moment'

declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => string
  }
}

export const fileNameToKey = (fileName: string): string => {
  return (
    fileName
      // remove extension
      .replace(/\.[^/.]+$/, '')
      // normalize unicode characters (é → e)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      // replace non-alphanumeric with spaces
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      // trim & replace spaces with _
      .trim()
      .replace(/\s+/g, '_')
      // lowercase
      .toLowerCase()
  )
}

export const ReportingIpcomModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  // const [file, setFile] = useState<File | null>()
  const file = componentProps?.file || null
  const [meta, setMeta] = useState<{ fields: string[]; delimiter: string; lineBreak: string } | null>(null)
  const [data, setData] = useState<any[] | null>(null)
  const [search, setSearch] = useState<string>('')

  const dataView = useMemo(() => {
    if (!data || data.length === 0) {
      return []
      // return <div className='bg-surface text-text w-full px-6 py-4'>No data available.</div>
    }

    let d = data

    if (search && search.length > 0) {
      d = data.filter((item) => {
        return Object.values(item).some((value) => {
          if (typeof value === 'string' || typeof value === 'number') {
            return value.toString().toLowerCase().includes(search.toLowerCase())
          }
          return false
        })
      })
    }

    return d
  }, [data, search])

  const onParseComplete = (result: Papa.ParseResult<any>) => {
    try {
      console.log(result)

      setMeta(result.meta as any)
      setData(result.data)
    } catch (e: any) {
      toastError(e?.message || 'Failed to parse CSV file, invalid file or format.')
    }
  }

  useEffect(() => {
    if (file) {
      Papa.parse(file, {
        complete: onParseComplete,
        header: true,
        skipEmptyLines: true
      })
    }
  }, [file])

  const onSubmit = async () => {
    try {
      if (!meta || !dataView || dataView.length === 0) {
        toastWarning('No data to import.')
        return
      }

      const csv = Papa.unparse(dataView!, {
        delimiter: meta?.delimiter!,
        header: true,
        columns: meta?.fields!,
        quotes: false,
        newline: meta?.lineBreak!
      })

      const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      let csvURL = null

      if (navigator.msSaveBlob) {
        csvURL = navigator.msSaveBlob(csvData, 'download.csv')
      } else {
        csvURL = window.URL.createObjectURL(csvData)
      }

      const tempLink = document.createElement('a')

      tempLink.href = csvURL
      tempLink.setAttribute('download', `${fileNameToKey(file.name || 'ipcom_report')}${moment().format('.DD.MM.YY.hh.mm')}.csv`)
      tempLink.click()

      onDismiss(null, ModalDismiss.Confirm)
      toastSuccess('Export success.')
    } catch (e: any) {
      // ignore, let mutation handle error
    }
  }

  return (
    <ModalContainer innerClasses='!min-h-[85lvh]'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between mb-2'>
          <ModalHeader subtitle='Parsed' title='IPCom Report' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
        <div>
          <PageResultsCount loading={!data} count={{ resultCount: dataView?.length || 0 }} />
          <ControlledInputDebounced
            type='text'
            placeholder='Start typing to search...'
            label='Search'
            id='searchVal'
            containerClassName='grow sm:grow-0 sm:min-w-[260px]'
            hideErrors
            value={search}
            onChange={(e: any) => setSearch(e?.target?.value || '')}
          />
        </div>
      </ModalHeaderContainer>
      <ModalBodyInfiniteScrollable classes='relative !pt-[120px] !pb-[150px]'>
        <div className='flex flex-col gap-[15px]'>
          {dataView &&
            dataView.length > 0 &&
            dataView.map((item, index) => (
              <div
                key={`ipcom-report-card-${index}`}
                className='w-full bg-surface border border-border rounded-3xl px-6 py-4 text-text flex flex-col items-center justify-between gap-[5px]'
              >
                {Object.entries(item).map(([key, value]) => {
                  if (!key || key.length === 0) return null

                  return (
                    <div key={`${index}-${key}`} className='w-full flex items-center justify-between gap-[10px]'>
                      <strong>{key}:</strong> {value as string}
                    </div>
                  )
                })}
              </div>
            ))}
        </div>
      </ModalBodyInfiniteScrollable>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRounded
            type='button'
            loading={!data}
            text='Export Filtered'
            className='!h-[30px] min-h-0 !min-w-[120px] !mt-0 !w-auto'
            callback={() => onSubmit()}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
